"use client";

import LoadingLogo from "@/components/Atoms/LoadingLogo";
import LocalityLabel from "@/components/Atoms/LocalityLabel";
import {
  FieldLabel,
  SectionHeader,
  SubHeader,
  Unit,
} from "@/components/Atoms/Typography";
import PixIcon from "@/components/Icons/PixIcon";
import { useDemoTheme } from "@/context/DemoThemeContext";
import { primaryColor } from "@/lib/theme";
import { OrderDetails } from "@/types/encomendas";
import { getAuthToken } from "@/utils/authUtils";
import { useEmbarcaLogValidation } from "@/utils/EmbarcaLogEnabledOperators";
import {
  formatAddress,
  formatCentsBRL,
  formatCurrencyBRL,
  formatDate,
  getPaymentMethodLabel,
  getStatusConfig,
  normalizeStatus,
  TRACKING_STEPS,
} from "@/utils/encomendasHelper";
import { maskPhone } from "@/utils/masks";
import { showErrorToast, showSuccessToast } from "@/utils/toastUtils";
import { AnimatePresence, motion } from "framer-motion";
import {
  ArrowLeft,
  Calendar,
  CheckCircle2,
  ChevronDown,
  Circle,
  CircleCheckBig,
  CircleDot,
  Clock,
  Copy,
  CreditCard,
  Download,
  Footprints,
  Headphones,
  Loader2,
  MapPin,
  Package as PackageIcon,
  Printer,
  Share2,
  Truck,
  Users,
  XCircle,
} from "lucide-react";
import dynamic from "next/dynamic";
import { useParams, useRouter, useSearchParams } from "next/navigation";
import useOrderPrefetchStore from "@/store/useOrderPrefetchStore";
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";

const BoxPreview3D = dynamic<{ length: string; width: string; height: string }>(
  () => import("@/components/Molecules/BoxPreview3D"),
  { ssr: false }
);

function Spawn({
  i,
  children,
}: {
  i: number;
  children: import("react").ReactNode;
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 14 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{
        duration: 0.3,
        ease: [0.25, 0.46, 0.45, 0.94],
        delay: i * 0.07,
      }}
    >
      {children}
    </motion.div>
  );
}

export default function OrderDetailsPage() {
  const hasAccess = useEmbarcaLogValidation();
  const { sac } = useDemoTheme();
  const params = useParams();
  const router = useRouter();
  const searchParams = useSearchParams();
  const orderId = params?.order_id as string;

  const [orderData, setOrderData] = useState<OrderDetails | null>(() =>
    orderId ? useOrderPrefetchStore.getState().consume(Number(orderId)) : null
  );
  const [isLoading, setIsLoading] = useState(orderData === null);
  const [isContinuingPayment, setIsContinuingPayment] = useState(false);
  const [isDownloadingTicket, setIsDownloadingTicket] = useState(false);
  const [isCancellingOrder, setIsCancellingOrder] = useState(false);
  const [hasAutoDownloaded, setHasAutoDownloaded] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [isHowToOpen, setIsHowToOpen] = useState(false);
  const [hasOpenedHowTo, setHasOpenedHowTo] = useState(false);

  const fetchOrder = async (force = false) => {
    if (!orderId) {
      router.push("/encomendas");
      return;
    }
    if (!force && orderData !== null) return;
    try {
      setIsLoading(true);
      const token = getAuthToken();
      if (!token) {
        router.push("/encomendas");
        return;
      }
      const response = await fetch(
        `${process.env.NEXT_PUBLIC_BASE_URL_API}/api/v1/embarca-log/orders/${orderId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (!response.ok) {
        throw new Error("Pedido não encontrado");
      }

      const data = await response.json();

      // awaiting_payment orders are no longer auto-redirected to /pagamento —
      // they render here so the user can review the order and resume payment
      // via the "Continuar pagamento" button below the route summary.
      setOrderData(data);
    } catch (error) {
      console.error("Erro ao buscar pedido:", error);
      router.push("/encomendas");
    } finally {
      setIsLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    void fetchOrder();
  }, [orderId]);

  useEffect(() => {
    if (!searchParams) return;
    const shouldDownload = searchParams.get("download") === "true";
    if (
      shouldDownload &&
      orderData &&
      !hasAutoDownloaded &&
      !isDownloadingTicket
    ) {
      setHasAutoDownloaded(true);
      downloadTicket();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderData, searchParams, hasAutoDownloaded, isDownloadingTicket]);

  useEffect(() => {
    if (!searchParams) return;
    if (hasAutoDownloaded && !isDownloadingTicket) {
      const newSearchParams = new URLSearchParams(searchParams.toString());
      newSearchParams.delete("download");
      const newUrl = `${window.location.pathname}${newSearchParams.toString() ? `?${newSearchParams.toString()}` : ""}`;
      router.replace(newUrl);
    }
  }, [hasAutoDownloaded, isDownloadingTicket, searchParams, router]);

  const handleCopyOrderId = () => {
    if (orderData?.minuta_id) {
      navigator.clipboard.writeText(orderData.minuta_id).then(() => {
        showSuccessToast("Código do pedido copiado!");
      });
    }
  };

  // Resume payment for an awaiting_payment order: seed the localStorage the
  // /pagamento screen reads, then navigate there flagged so it shows a simple
  // "back to this order" header instead of the full checkout stepper.
  const handleContinuePayment = () => {
    if (!orderData || isContinuingPayment) return;
    setIsContinuingPayment(true);
    try {
      const sender = orderData.participants?.find(
        (p) => p.participant_type === "sender"
      );
      localStorage.setItem(
        "embarca-log.order",
        JSON.stringify({
          id: orderData.id,
          total_cents: orderData.total_cents,
          holder_name: sender?.name ?? "",
          holder_document: sender?.document ?? "",
          checkout_id: orderData.checkout_payments_id,
        })
      );
      // Seed route summary so the payment screen doesn't flash "Carregando..."
      // before its own checkout fetch resolves.
      if (orderData.origin)
        localStorage.setItem(
          "embarca-log.origin",
          JSON.stringify({
            name: orderData.origin.name,
            state: orderData.origin.state,
            address: orderData.origin.address,
          })
        );
      if (orderData.destination)
        localStorage.setItem(
          "embarca-log.destination",
          JSON.stringify({
            name: orderData.destination.name,
            state: orderData.destination.state,
            address: orderData.destination.address,
          })
        );
    } catch (e) {
      console.error("Falha ao preparar o pagamento:", e);
    }
    router.push(`/encomendas/pagamento?from=order:${orderData.id}`);
  };

  const downloadTicket = async () => {
    if (isDownloadingTicket || !orderData) return;
    try {
      setIsDownloadingTicket(true);
      const token = getAuthToken();
      if (!token) return;
      const response = await fetch(
        `${process.env.NEXT_PUBLIC_BASE_URL_API}/api/v1/embarca-log/orders/${orderData.id}/ticket`,
        { headers: { "X-API-TOKEN": token, Authorization: `Bearer ${token}` } }
      );
      if (!response.ok)
        throw new Error(`Erro ao baixar etiqueta: ${response.status}`);
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "etiqueta.pdf";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Erro ao baixar etiqueta:", error);
    } finally {
      setIsDownloadingTicket(false);
    }
  };

  const handleShare = () => {
    if (!orderData?.minuta_id) return;

    const trackingUrl = `${window.location.origin}/encomendas/rastreio/${orderData.minuta_id}`;

    if (navigator.share) {
      navigator
        .share({
          title: "Rastreio do envio",
          text: `Código da encomenda: ${orderData.minuta_id}`,
          url: trackingUrl,
        })
        .catch(() => {});
    } else {
      navigator.clipboard
        .writeText(trackingUrl)
        .then(() =>
          showSuccessToast("Link copiado para a área de transferência!")
        )
        .catch(() => {});
    }
  };

  const handleCancelOrder = async () => {
    if (!orderData || isCancellingOrder) return;
    try {
      setIsCancellingOrder(true);
      const token = getAuthToken();
      if (!token) {
        showErrorToast("Erro de autenticação. Faça login novamente.");
        return;
      }
      const response = await fetch(
        `${process.env.NEXT_PUBLIC_BASE_URL_API}/api/v1/embarca-log/orders/${orderData.id}/refund`,
        { method: "POST", headers: { Authorization: `Bearer ${token}` } }
      );
      if (!response.ok) throw new Error("Falha ao cancelar a encomenda.");
      showSuccessToast("Encomenda cancelada com sucesso.");
      setShowCancelModal(false);
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: "smooth" });
      }, 150);
      await fetchOrder(true);
    } catch (error) {
      console.error("Erro ao cancelar encomenda:", error);
      showErrorToast(
        "Ocorreu um erro ao cancelar a encomenda. Tente novamente."
      );
    } finally {
      setIsCancellingOrder(false);
    }
  };

  const calculateDeliveryDays = (): string => {
    if (!orderData?.created_at || !orderData?.estimated_arrival)
      return "3 dias";
    const diffDays = Math.ceil(
      Math.abs(
        new Date(orderData.estimated_arrival).getTime() -
          new Date(orderData.created_at).getTime()
      ) /
        (1000 * 60 * 60 * 24)
    );
    return `${diffDays} ${diffDays === 1 ? "dia" : "dias"}`;
  };

  const getTotalPackages = (): number => {
    if (!orderData?.packages) return 0;
    return orderData.packages.reduce((sum, pkg) => sum + pkg.amount, 0);
  };

  if (!hasAccess) return null;

  if (isLoading) {
    return (
      <main className="min-h-screen pb-12 pt-4 md:pt-6">
        <div className="max-w-[832px] mx-auto px-4 md:px-6 flex flex-col items-center justify-center gap-6 py-32">
          <LoadingLogo />
          <div className="h-px w-48 bg-line" />
          <div className="flex gap-1.5 items-end">
            {[0, 1, 2].map((i) => (
              <span
                key={i}
                className="w-2 h-2 rounded-full animate-bounce"
                style={{
                  backgroundColor: primaryColor,
                  animationDelay: `${i * 0.15}s`,
                }}
              />
            ))}
          </div>
        </div>
      </main>
    );
  }

  if (!orderData) return null;

  const currentStatus = normalizeStatus(orderData.status);
  const isStepCompleted = (stepStatus: number) => currentStatus >= stepStatus;
  const isStepCurrent = (stepStatus: number) => currentStatus === stepStatus;
  const paymentMethod = orderData.checkout_payment?.payment_method || "pix";
  const paymentLabel = getPaymentMethodLabel(paymentMethod);
  const statusConfig = getStatusConfig(currentStatus);
  const isCancelled = currentStatus === 0 || currentStatus === 9;

  return (
    <main className="min-h-screen pb-12 pt-4 md:pt-6">
      <div className="max-w-[832px] mx-auto px-4 md:px-6 space-y-4">
        {/* Back */}
        <Spawn i={0}>
          <button
            id="btn-order-back-envios"
            onClick={() => router.push("/encomendas/envios")}
            className="flex items-center gap-2 text-sm text-slate hover:text-ink transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Voltar para meus envios
          </button>
        </Spawn>

        {/* Hero card */}
        <Spawn i={1}>
          <div className="bg-white border border-line rounded-2xl overflow-hidden">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 px-6 py-5">
              <div className="flex items-center gap-4">
                <div
                  className="w-10 h-10 rounded-control flex items-center justify-center shrink-0"
                  style={{ backgroundColor: `${primaryColor}1A` }}
                >
                  <PackageIcon
                    className="w-5 h-5"
                    style={{ color: primaryColor }}
                  />
                </div>
                <p className="text-head font-bold text-ink leading-tight">
                  {orderData.minuta_id}
                </p>
              </div>
              <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                <span
                  className="inline-flex items-center h-8 px-3 rounded-full text-fine font-semibold self-start sm:self-auto"
                  style={{
                    color: statusConfig.color,
                    backgroundColor: statusConfig.bg,
                  }}
                >
                  {statusConfig.label}
                </span>
                <div className="flex items-center gap-2">
                  <button
                    id="btn-order-copy-code"
                    onClick={handleCopyOrderId}
                    className="h-8 px-3 rounded-lg border border-line text-fine font-semibold text-slate hover:text-ink hover:border-mist transition-colors inline-flex items-center gap-1.5"
                  >
                    <Copy className="w-3.5 h-3.5" /> Copiar código
                  </button>
                  <button
                    id="btn-order-share"
                    onClick={handleShare}
                    className="h-8 px-3 rounded-lg border border-line text-fine font-semibold text-slate hover:text-ink hover:border-mist transition-colors inline-flex items-center gap-1.5"
                  >
                    <Share2 className="w-3.5 h-3.5" /> Compartilhar
                  </button>
                </div>
              </div>
            </div>
          </div>
        </Spawn>

        {/* Info pills — hidden while awaiting payment (status 1) */}
        {currentStatus !== 1 && (
          <Spawn i={2}>
            <div className="grid grid-cols-3 gap-3">
              {[
                {
                  Icon: Calendar,
                  label: "Compra",
                  value: formatDate(orderData.created_at),
                  unit: "",
                },
                {
                  Icon: Clock,
                  label: "Prazo",
                  value: calculateDeliveryDays(),
                  unit: "",
                },
                {
                  Icon: PackageIcon,
                  label: "Pacotes",
                  value: String(getTotalPackages()),
                  unit: "un",
                },
              ].map(({ Icon, label, value, unit }) => (
                <div
                  key={label}
                  className="bg-white border border-line rounded-2xl p-4 flex flex-col items-center justify-center gap-1 text-center"
                >
                  <div
                    className="w-8 h-8 rounded-chip flex items-center justify-center mb-1"
                    style={{ backgroundColor: `${primaryColor}1A` }}
                  >
                    <Icon className="w-4 h-4" style={{ color: primaryColor }} />
                  </div>
                  <FieldLabel>{label}</FieldLabel>
                  <p className="text-fine font-bold text-ink">
                    {value}
                    {unit ? <Unit className="ml-0.5">{unit}</Unit> : null}
                  </p>
                </div>
              ))}
            </div>
          </Spawn>
        )}

        {/* Payment — shown only while awaiting payment (status 1) */}
        {currentStatus === 1 && (
          <Spawn i={3}>
            <>
              <style>{`
                @keyframes pay-spin-border {
                  from { --pay-border-angle: 0deg; }
                  to   { --pay-border-angle: 360deg; }
                }
                @property --pay-border-angle {
                  syntax: '<angle>';
                  initial-value: 0deg;
                  inherits: false;
                }
                .pay-border-animated {
                  background: conic-gradient(
                    from var(--pay-border-angle),
                    #E2E5E9 0%,
                    #E2E5E9 40%,
                    #9DAFC4 50%,
                    #E2E5E9 60%,
                    #E2E5E9 100%
                  );
                  animation: pay-spin-border 2.5s linear infinite;
                  padding: 1.5px;
                  border-radius: 1rem;
                }
              `}</style>
              <div className="pay-border-animated">
                <div className="bg-white rounded-[calc(1rem-1.5px)] pb-6">
                  <SectionHeader
                    title="Pagamento"
                    icon={
                      <CreditCard
                        className="w-5 h-5"
                        style={{ color: primaryColor }}
                      />
                    }
                  />
                  <div className="px-6 pt-5">
                    <div className="flex items-center gap-3 p-4 rounded-control bg-warning-soft mb-5">
                      <Clock className="w-5 h-5 shrink-0 text-warning" />
                      <p className="text-fine font-semibold text-warning">
                        Este envio está aguardando pagamento. Conclua o
                        pagamento para que possamos processá-lo.
                      </p>
                    </div>

                    <div className="space-y-2.5 mb-5">
                      <div className="flex items-center justify-between">
                        <span className="text-fine text-slate">Subtotal</span>
                        <span className="text-fine font-semibold text-ink">
                          {formatCentsBRL(orderData.fare_cents)}
                        </span>
                      </div>
                      {(orderData.discount_cents ?? 0) > 0 && (
                        <div className="flex items-center justify-between">
                          <span className="text-fine text-slate">
                            {orderData.discount_code
                              ? `Cupom ${orderData.discount_code}`
                              : "Desconto"}
                            {orderData.discount_percent
                              ? ` (${orderData.discount_percent}%)`
                              : ""}
                          </span>
                          <span className="text-fine font-semibold text-danger">
                            -{formatCentsBRL(orderData.discount_cents!)}
                          </span>
                        </div>
                      )}
                      <div className="h-px bg-line" />
                      <div className="flex items-center justify-between">
                        <span className="text-fine font-bold text-ink">
                          Total a pagar
                        </span>
                        <span className="text-head font-bold text-ink">
                          {formatCentsBRL(orderData.total_cents)}
                        </span>
                      </div>
                    </div>

                    <button
                      id="btn-order-continue-payment"
                      type="button"
                      onClick={handleContinuePayment}
                      disabled={isContinuingPayment}
                      className="w-full h-12 rounded-control text-sm font-bold text-white hover:opacity-90 transition-opacity disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                      style={{ backgroundColor: primaryColor }}
                    >
                      {isContinuingPayment ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <CreditCard className="w-4 h-4" />
                      )}
                      Continuar pagamento
                    </button>
                  </div>
                </div>
              </div>
            </>
          </Spawn>
        )}

        {/* Route card */}
        <Spawn i={4}>
          <div className="bg-white border border-line rounded-2xl pb-6">
            <SectionHeader
              title="Origem e destino"
              icon={
                <MapPin className="w-5 h-5" style={{ color: primaryColor }} />
              }
            />

            <div className="flex gap-4 px-6 pt-5">
              <div className="flex flex-col items-center shrink-0">
                <div
                  className="w-8 h-8 rounded-chip flex items-center justify-center"
                  style={{ backgroundColor: `${primaryColor}1A` }}
                >
                  <MapPin className="w-4 h-4" style={{ color: primaryColor }} />
                </div>
                <div
                  className="w-px flex-1 bg-line my-2 border-l-2 border-dashed border-mist"
                  style={{ minHeight: 32 }}
                />
                <div
                  className="w-8 h-8 rounded-chip flex items-center justify-center"
                  style={{ backgroundColor: `${primaryColor}1A` }}
                >
                  <MapPin className="w-4 h-4" style={{ color: primaryColor }} />
                </div>
              </div>
              <div className="flex-1 space-y-4">
                <div>
                  <FieldLabel className="mb-0.5">Envio</FieldLabel>
                  <p className="text-body font-bold text-ink">
                    <LocalityLabel
                      name={orderData.origin.name}
                      cityName={
                        (orderData.origin as { city_name?: string }).city_name
                      }
                      state={orderData.origin.state}
                    />
                  </p>
                  <p className="text-fine text-slate mt-0.5">
                    {formatAddress(orderData.origin)}
                  </p>
                </div>
                <div>
                  <FieldLabel className="mb-0.5">Retirada</FieldLabel>
                  <p className="text-body font-bold text-ink">
                    <LocalityLabel
                      name={orderData.destination.name}
                      cityName={
                        (orderData.destination as { city_name?: string })
                          .city_name
                      }
                      state={orderData.destination.state}
                    />
                  </p>
                  <p className="text-fine text-slate mt-0.5">
                    {formatAddress(orderData.destination)}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </Spawn>

        {/* How to proceed accordion — shown only when payment confirmed (status 2) */}
        {currentStatus === 2 && (
          <Spawn i={5}>
            <>
              <style>{`
                @keyframes spin-border {
                  from { --border-angle: 0deg; }
                  to   { --border-angle: 360deg; }
                }
                @property --border-angle {
                  syntax: '<angle>';
                  initial-value: 0deg;
                  inherits: false;
                }
                .how-to-border-animated {
                  background: conic-gradient(
                    from var(--border-angle),
                    #E2E5E9 0%,
                    #E2E5E9 40%,
                    #9DAFC4 50%,
                    #E2E5E9 60%,
                    #E2E5E9 100%
                  );
                  animation: spin-border 2.5s linear infinite;
                  padding: 1.5px;
                  border-radius: 1rem;
                }
                .how-to-border-static {
                  background: #E2E5E9;
                  padding: 1px;
                  border-radius: 1rem;
                }
              `}</style>
              <div
                className={
                  hasOpenedHowTo
                    ? "how-to-border-static"
                    : "how-to-border-animated"
                }
              >
                <div className="bg-white rounded-[calc(1rem-1.5px)] overflow-hidden">
                  <button
                    id="btn-order-toggle-how-to"
                    type="button"
                    onClick={() => {
                      setIsHowToOpen((v) => !v);
                      setHasOpenedHowTo(true);
                    }}
                    className="w-full flex items-center justify-between gap-3 px-6 pt-5 pb-4 text-left"
                  >
                    <div className="flex min-w-0 flex-1 items-center gap-3">
                      <div
                        className="flex size-10 shrink-0 items-center justify-center rounded-control"
                        style={{ backgroundColor: `${primaryColor}1A` }}
                      >
                        <Footprints
                          className="w-5 h-5"
                          style={{ color: primaryColor }}
                        />
                      </div>
                      <h2 className="min-w-0 flex-1 text-lede font-bold text-ink">
                        Como prosseguir com o envio
                      </h2>
                    </div>
                    <ChevronDown
                      className="w-5 h-5 text-slate shrink-0 transition-transform duration-300"
                      style={{
                        transform: isHowToOpen
                          ? "rotate(180deg)"
                          : "rotate(0deg)",
                      }}
                    />
                  </button>

                  <div
                    className="grid transition-[grid-template-rows] duration-300 ease-in-out"
                    style={{ gridTemplateRows: isHowToOpen ? "1fr" : "0fr" }}
                  >
                    <div className="overflow-hidden">
                      <div className="px-6 pb-6">
                        <div className="h-px bg-line mb-6" />
                        <div className="space-y-0">
                          {[
                            {
                              Icon: Printer,
                              step: "Etapa 01",
                              title: "Etiqueta e declaração de conteúdo",
                              body: isDownloadingTicket ? (
                                <span className="inline-flex items-center gap-2 text-slate">
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                  Gerando sua etiqueta...
                                </span>
                              ) : (
                                <>
                                  Clique{" "}
                                  <button
                                    id="btn-order-download-ticket-inline"
                                    onClick={downloadTicket}
                                    className="underline hover:opacity-80 transition-opacity font-semibold"
                                    style={{ color: primaryColor }}
                                  >
                                    aqui
                                  </button>{" "}
                                  e imprima sua etiqueta e declaração de
                                  conteúdo.
                                </>
                              ),
                            },
                            {
                              Icon: PackageIcon,
                              step: "Etapa 02",
                              title: "Prepare seu envio",
                              body: "Cole sua etiqueta em seu envio.",
                            },
                            {
                              Icon: Footprints,
                              step: "Etapa 03",
                              title: "Vá até o local do envio",
                              body: "Vá ao local de envio e entregue seu pacote e sua declaração assinada.",
                            },
                            {
                              Icon: CircleCheckBig,
                              step: "Etapa 04",
                              title: "Despache seu envio",
                              body: "Recolheremos seu envio e a partir daqui é só acompanhar seu status em nosso rastreio.",
                            },
                          ].map((s, i, arr) => (
                            <div key={i} className="flex gap-4">
                              <div className="flex flex-col items-center shrink-0">
                                <div className="w-9 h-9 flex items-center justify-center shrink-0">
                                  <s.Icon
                                    className="w-6 h-6"
                                    strokeWidth={1.75}
                                    style={{ color: primaryColor }}
                                  />
                                </div>
                                {i < arr.length - 1 && (
                                  <div
                                    className="w-px flex-1 bg-line my-2"
                                    style={{ minHeight: 24 }}
                                  />
                                )}
                              </div>
                              <div
                                className={`flex-1 min-w-0 ${i < arr.length - 1 ? "pb-6" : ""}`}
                              >
                                <FieldLabel className="mb-0.5">
                                  {s.step}
                                </FieldLabel>
                                <p className="text-body font-bold text-ink mb-1">
                                  {s.title}
                                </p>
                                <p className="text-sm text-slate leading-relaxed">
                                  {s.body}
                                </p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </>
          </Spawn>
        )}

        {/* Tracking card — hidden for refunded (9) and awaiting payment (1) */}
        {currentStatus !== 9 && currentStatus !== 1 && (
          <Spawn i={6}>
            <div className="bg-white border border-line rounded-2xl pb-6">
              <SectionHeader
                title="Rastreio"
                icon={
                  <Truck className="w-5 h-5" style={{ color: primaryColor }} />
                }
              />

              <div className="space-y-0 px-6 pt-5">
                {TRACKING_STEPS.map((step, i) => {
                  const completed = isStepCompleted(step.status);
                  const current = isStepCurrent(step.status);
                  const isLast = i === TRACKING_STEPS.length - 1;
                  const nextCompleted =
                    !isLast && isStepCompleted(TRACKING_STEPS[i + 1].status);

                  return (
                    <div key={`${step.label}-${i}`} className="flex gap-4">
                      <div className="flex flex-col items-center shrink-0">
                        <div className="w-9 h-9 flex items-center justify-center">
                          {current && !isLast ? (
                            <div className="relative flex items-center justify-center">
                              <span
                                className="absolute inline-flex h-6 w-6 rounded-full opacity-30 animate-ping"
                                style={{ backgroundColor: primaryColor }}
                              />
                              <CircleDot
                                className="w-6 h-6 relative z-10"
                                style={{ color: primaryColor }}
                                strokeWidth={2.5}
                              />
                            </div>
                          ) : completed ? (
                            <CheckCircle2
                              className="w-6 h-6"
                              style={{ color: primaryColor }}
                              strokeWidth={1.75}
                            />
                          ) : (
                            <Circle
                              className="w-6 h-6 text-mist"
                              strokeWidth={1.75}
                            />
                          )}
                        </div>
                        {!isLast && (
                          <div
                            className="w-0.5 flex-1 my-1 rounded-full"
                            style={{
                              backgroundColor: nextCompleted
                                ? primaryColor
                                : "#E2E5E9",
                              minHeight: 24,
                            }}
                          />
                        )}
                      </div>
                      <div
                        className={`flex-1 min-w-0 ${!isLast ? "pb-5" : ""}`}
                      >
                        <FieldLabel className="mb-0.5">{step.label}</FieldLabel>
                        <p
                          className={
                            current
                              ? "text-lede font-bold text-ink"
                              : completed
                                ? "text-body font-semibold text-ink"
                                : "text-body text-mist"
                          }
                        >
                          {step.title}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </Spawn>
        )}

        {/* Packages card */}
        {orderData.packages?.length > 0 && (
          <Spawn i={7}>
            <div className="bg-white border border-line rounded-2xl pb-6">
              <SectionHeader
                title="Resumo do envio"
                icon={
                  <PackageIcon
                    className="w-5 h-5"
                    style={{ color: primaryColor }}
                  />
                }
              />

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 px-6 pt-6">
                {orderData.packages.map((pkg, idx) => {
                  const lengthCm = Math.round(parseFloat(pkg.length) * 100);
                  const widthCm = Math.round(parseFloat(pkg.width) * 100);
                  const heightCm = Math.round(parseFloat(pkg.height) * 100);
                  const weightKg = parseFloat(pkg.weight);
                  const valueBRL = parseFloat(pkg.value);
                  const itemCount =
                    pkg.items?.reduce((s, i) => s + i.quantity, 0) ??
                    pkg.amount;

                  return (
                    <div
                      key={pkg.id}
                      className="bg-white rounded-control border border-line p-4 flex flex-col gap-3"
                    >
                      <p className="text-fine font-bold text-ink">
                        Pacote {String(idx + 1).padStart(2, "0")}
                      </p>
                      <div className="h-px bg-line" />

                      <div className="w-full" style={{ aspectRatio: "11/6" }}>
                        <BoxPreview3D
                          length={String(lengthCm)}
                          width={String(widthCm)}
                          height={String(heightCm)}
                        />
                      </div>

                      <div className="h-px bg-line" />

                      <div className="space-y-1.5">
                        {[
                          ["Comprimento", String(lengthCm), "cm"],
                          ["Largura", String(widthCm), "cm"],
                          ["Altura", String(heightCm), "cm"],
                          ["Peso", String(weightKg), "kg"],
                          ["Valor declarado", formatCurrencyBRL(valueBRL), ""],
                          ["Volumes", String(itemCount), "uni"],
                        ].map(([label, val, unit]) => (
                          <div
                            key={label}
                            className="flex justify-between items-center"
                          >
                            <FieldLabel>{label}</FieldLabel>
                            <span className="text-fine font-semibold text-ink">
                              {val}
                              {unit ? (
                                <Unit className="ml-0.5">{unit}</Unit>
                              ) : null}
                            </span>
                          </div>
                        ))}
                      </div>

                      {pkg.items && pkg.items.length > 0 && (
                        <>
                          <div className="h-px bg-line" />
                          <div className="space-y-2">
                            <SubHeader>Itens no pacote</SubHeader>
                            {pkg.items.map((item, itemIdx) => (
                              <div
                                key={item.id}
                                className="rounded-control border border-line px-3 py-2.5 flex flex-col gap-2"
                              >
                                <div className="flex items-center gap-3">
                                  <div
                                    className="w-8 h-8 rounded-chip flex items-center justify-center shrink-0"
                                    style={{
                                      backgroundColor: `${primaryColor}1A`,
                                    }}
                                  >
                                    <PackageIcon
                                      className="w-4 h-4"
                                      style={{ color: primaryColor }}
                                    />
                                  </div>
                                  <p className="flex-1 min-w-0 text-fine font-semibold text-ink truncate">
                                    {item.name?.trim() ||
                                      `Item ${String(itemIdx + 1).padStart(2, "0")}`}
                                  </p>
                                  <span className="text-fine font-semibold text-ink shrink-0">
                                    {item.quantity}
                                    <Unit className="ml-0.5">uni</Unit>
                                  </span>
                                </div>
                                <div className="space-y-1 pl-11">
                                  <div className="flex justify-between items-center gap-2">
                                    <span className="text-micro text-slate">
                                      Valor unitário
                                    </span>
                                    <span className="text-micro font-semibold text-ink">
                                      {formatCurrencyBRL(
                                        parseFloat(item.value)
                                      )}
                                    </span>
                                  </div>
                                  <div className="flex justify-between items-center gap-2">
                                    <span className="text-micro text-slate">
                                      Peso/volume
                                    </span>
                                    <span className="text-micro font-semibold text-ink">
                                      {item.weight_volume || "—"}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </Spawn>
        )}

        {/* Participants card */}
        {orderData.participants && orderData.participants.length > 0 && (
          <Spawn i={8}>
            <div className="bg-white border border-line rounded-2xl pb-6">
              <SectionHeader
                title="Remetente e destinatário"
                icon={
                  <Users className="w-5 h-5" style={{ color: primaryColor }} />
                }
              />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 px-6 pt-5">
                {orderData.participants.map((p) => (
                  <div key={p.id} className="space-y-1">
                    <FieldLabel>
                      {p.participant_type === "sender"
                        ? "Remetente"
                        : "Destinatário"}
                    </FieldLabel>
                    <p className="text-body font-bold text-ink">{p.name}</p>
                    <p className="text-fine text-slate">{p.email}</p>
                    <p className="text-fine text-slate">
                      {maskPhone(p.phone)}
                    </p>
                    <p className="text-fine text-slate">
                      {p.address}
                      {p.number ? `, ${p.number}` : ""}
                      {p.complement ? ` ${p.complement}` : ""}, {p.district},{" "}
                      {p.city} - {p.state}, {p.cep}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </Spawn>
        )}

        {/* Payment card — hidden while awaiting payment (status 1) */}
        {currentStatus !== 1 && (
          <Spawn i={9}>
            <div className="bg-white border border-line rounded-2xl pb-6">
              <SectionHeader
                title="Dados do pagamento"
                icon={
                  <CreditCard
                    className="w-5 h-5"
                    style={{ color: primaryColor }}
                  />
                }
              />

              <div className="flex items-center gap-3 p-3 rounded-control border border-line mx-6 mt-5 mb-4">
                <div
                  className="w-9 h-9 rounded-chip flex items-center justify-center shrink-0"
                  style={{ backgroundColor: `${primaryColor}1A` }}
                >
                  {paymentMethod === "pix" ? (
                    <PixIcon size={16} color={primaryColor} />
                  ) : (
                    <CreditCard
                      className="w-4 h-4"
                      style={{ color: primaryColor }}
                    />
                  )}
                </div>
                <div className="flex-1">
                  <p className="text-fine font-bold text-ink">{paymentLabel}</p>
                  {orderData.checkout_payment?.card_brand && (
                    <p className="text-fine text-slate">
                      •••• •••• ••••{" "}
                      {orderData.checkout_payment.card_last_digits}
                    </p>
                  )}
                  {orderData.checkout_payment?.installments != null &&
                    orderData.checkout_payment.installments > 0 &&
                    paymentMethod !== "pix" && (
                      <p className="text-fine text-slate">
                        {orderData.checkout_payment.installments}x{" "}
                        {(orderData.checkout_payment.installment_fee_cents ??
                          0) > 0
                          ? "com juros"
                          : "sem juros"}
                      </p>
                    )}
                </div>
              </div>

              <div className="space-y-2.5 px-6">
                <div className="flex justify-between items-center">
                  <span className="text-fine text-slate">Envio Express</span>
                  <span className="text-fine font-semibold text-ink">
                    {formatCentsBRL(orderData.fare_cents)}
                  </span>
                </div>
                {(orderData.checkout_payment?.installment_fee_cents ?? 0) >
                  0 && (
                  <div className="flex justify-between items-center">
                    <span className="text-fine text-slate">
                      Juros ({orderData.checkout_payment!.installments}x
                      parcelas)
                    </span>
                    <span className="text-fine font-semibold text-ink">
                      {formatCentsBRL(
                        orderData.checkout_payment!.installment_fee_cents!
                      )}
                    </span>
                  </div>
                )}
                {(orderData.discount_cents ?? 0) > 0 && (
                  <div className="flex justify-between items-center">
                    <span className="text-fine text-slate">
                      {orderData.discount_code
                        ? `Cupom ${orderData.discount_code}`
                        : "Desconto"}
                      {orderData.discount_percent
                        ? ` (${orderData.discount_percent}%)`
                        : ""}
                    </span>
                    <span className="text-fine font-semibold text-danger">
                      -{formatCentsBRL(orderData.discount_cents!)}
                    </span>
                  </div>
                )}
                <div className="h-px bg-line" />
                <div className="flex justify-between items-center">
                  <span className="text-fine font-bold text-ink">
                    Total pago
                  </span>
                  <span className="text-body font-bold text-ink">
                    {formatCentsBRL(
                      currentStatus === 0 ? 0 : orderData.total_cents
                    )}
                  </span>
                </div>
                {currentStatus === 9 && (
                  <div className="flex justify-between items-center">
                    <span className="text-fine text-slate">
                      Total reembolsado
                    </span>
                    <span className="text-fine font-semibold text-ink">
                      {formatCentsBRL(orderData.total_cents)}
                    </span>
                  </div>
                )}
              </div>

              {isCancelled && (
                <div className="mx-6 mt-4 flex items-start gap-3 p-3 rounded-control bg-danger-soft border border-danger-soft">
                  <XCircle
                    className="w-4 h-4 text-danger shrink-0 mt-0.5"
                    strokeWidth={1.75}
                  />
                  <div>
                    <p className="text-fine font-bold text-danger">
                      {currentStatus === 9
                        ? "Pedido reembolsado"
                        : "Pedido cancelado"}
                    </p>
                    <p className="text-micro text-danger opacity-80">
                      {currentStatus === 9
                        ? "Este pedido foi cancelado e o valor foi reembolsado."
                        : "Este pedido foi cancelado por falta de pagamento."}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </Spawn>
        )}

        {/* Actions */}
        {currentStatus !== 0 && currentStatus !== 1 && currentStatus !== 9 && (
          <Spawn i={10}>
            <button
              id="btn-order-download-ticket"
              onClick={downloadTicket}
              disabled={isDownloadingTicket}
              className="w-full h-11 rounded-xl text-sm font-bold text-white hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2"
              style={{ backgroundColor: primaryColor }}
            >
              {isDownloadingTicket ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Download className="w-4 h-4" />
              )}
              {isDownloadingTicket
                ? "Baixando..."
                : "Baixar etiqueta e declaração de conteúdo"}
            </button>
          </Spawn>
        )}

        {currentStatus === 2 && (
          <Spawn i={11}>
            <button
              id="btn-order-cancel-open"
              onClick={() => setShowCancelModal(true)}
              disabled={isCancellingOrder}
              className="w-full h-11 rounded-xl text-sm font-bold text-danger border-2 border-danger hover:bg-danger-soft transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancelar encomenda
            </button>
          </Spawn>
        )}

        {/* Help card */}
        <Spawn i={12}>
          <div className="bg-white border border-line rounded-2xl p-5 flex flex-col items-center gap-3 text-center">
            <div
              className="w-9 h-9 rounded-control flex items-center justify-center shrink-0"
              style={{ backgroundColor: `${primaryColor}1A` }}
            >
              <Headphones className="w-5 h-5" style={{ color: primaryColor }} />
            </div>
            <div>
              <p className="text-body font-bold text-ink">Precisa de ajuda?</p>
              <p className="text-fine text-slate mt-0.5">
                Entre em contato pelo telefone:{" "}
                <span className="font-semibold text-ink">{sac || "-"}</span>
              </p>
            </div>
          </div>
        </Spawn>
      </div>

      {/* Cancel modal */}
      {createPortal(
        <AnimatePresence>
          {showCancelModal && (
            <>
              <motion.div
                key="cancel-backdrop"
                className="fixed inset-0 z-[200] bg-black/50"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.22, ease: [0.25, 0.46, 0.45, 0.94] }}
                onClick={() => {
                  if (!isCancellingOrder) setShowCancelModal(false);
                }}
              />
              <div className="fixed inset-0 z-[201] flex items-center justify-center p-4 pointer-events-none">
                <motion.div
                  key="cancel-panel"
                  className="pointer-events-auto bg-white rounded-2xl p-6 max-w-sm w-full space-y-4 shadow-xl"
                  initial={{ opacity: 0, scale: 0.88 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.88 }}
                  transition={{
                    type: "spring",
                    damping: 28,
                    stiffness: 360,
                    mass: 0.85,
                  }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-control flex items-center justify-center bg-danger-soft">
                      <XCircle
                        className="w-5 h-5 text-danger"
                        strokeWidth={1.75}
                      />
                    </div>
                    <h2 className="text-lede font-bold text-ink">
                      Cancelar encomenda
                    </h2>
                  </div>
                  <div className="h-px bg-line" />
                  <p className="text-sm text-slate">
                    Tem certeza que deseja cancelar esta encomenda? Esta ação
                    não pode ser desfeita e{" "}
                    <strong>
                      o valor pago será reembolsado instantaneamente
                    </strong>
                    .
                  </p>
                  <div className="flex gap-3">
                    <button
                      id="btn-order-cancel-dismiss"
                      type="button"
                      onClick={() => setShowCancelModal(false)}
                      disabled={isCancellingOrder}
                      className="flex-1 h-11 rounded-xl border border-line text-sm font-bold text-ink hover:bg-ground transition-colors disabled:opacity-50"
                    >
                      Voltar
                    </button>
                    <button
                      id="btn-order-cancel-confirm"
                      type="button"
                      onClick={handleCancelOrder}
                      disabled={isCancellingOrder}
                      className="flex-1 h-11 rounded-xl bg-danger text-sm font-bold text-white hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2"
                    >
                      {isCancellingOrder && (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      )}
                      {isCancellingOrder ? "Cancelando..." : "Cancelar"}
                    </button>
                  </div>
                </motion.div>
              </div>
            </>
          )}
        </AnimatePresence>,
        document.body
      )}
    </main>
  );
}
